import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { paginateDefault } from 'components/pages/processing/state.default';
import { UseFormReturn } from 'react-hook-form';
import {
    fetchDeleteProcessing,
    fetchProcessingList,
    updateProcessingOutput,
    updateProcessingIntput,
    fetchProcessingListDetail,
} from 'components/pages/processing/thunk/processingTaskThunk';
import { TYPE_PROCESSING_TASK_DETAIL } from 'utils/constants';
import { SCREEN_TYPE } from 'utils/enums';

export enum FormProcessingType {
    ProcessingInput = 'processing_input',
    ProcessingOutput = 'processing_output',
}
export enum TableColumnsType {
    List = 'table_list',
    Detail = 'table_detals',
}

const initialState: ProcessingTaskState = {
    // Detail
    formType: FormProcessingType.ProcessingInput,
    formRegister: undefined,
    dataTableInput: [],
    selectedItemInput: undefined,
    selectedItemsInput: [],
    dataTableOutput: [],
    selectedItemOutput: undefined,
    selectedItemsOutput: [],
    dataDeletedOutput: [],
    dataDeletedInput: [],
    // List
    dataTable: [],
    paginateMetaData: paginateDefault,
    selectedItem: undefined,
    selectedItems: [],
    // Common
    isDetailLayout: false,
    isShowDeleteButton: false,
    isSubmit: false,
    formSearch: undefined,
    screenMode: SCREEN_TYPE.list,
    getKeywords: [],
    sort: 'task_id:asc',
};

const resetProcessingState = (state: Draft<ProcessingTaskState>) => {
    state.isShowDeleteButton = false;
    state.selectedItems = [];
    state.isDetailLayout = false;
    state.dataDeletedOutput = [];
    state.dataDeletedInput = [];
    state.dataTableInput = [];
    state.dataTableOutput = [];
    state.formType = FormProcessingType.ProcessingInput;
};

export const processingTaskSlice = createSlice({
    name: 'processingTask',
    initialState,
    reducers: {
        // Detail
        setFormType: (state, action: PayloadAction<FormProcessingType>) => {
            state.formType = action.payload;
        },
        setFormRegister: (state, action: PayloadAction<UseFormReturn<IRegisterProcessing>>) => {
            state.formRegister = action.payload;
        },
        setDataTableInput: (state, action: PayloadAction<ProcessingTableDetail[]>) => {
            state.dataTableInput = action.payload;
        },
        setSelectedItemInput: (state, action: PayloadAction<ProcessingTableDetail | undefined>) => {
            state.selectedItemInput = action.payload;
        },
        setSelectedItemsInput: (state, action: PayloadAction<number[]>) => {
            state.selectedItemsInput = action.payload;
        },
        setDataTableOutput: (state, action: PayloadAction<ProcessingTableDetail[]>) => {
            state.dataTableOutput = action.payload;
        },
        setSelectedItemOutput: (
            state,
            action: PayloadAction<ProcessingTableDetail | undefined>,
        ) => {
            state.selectedItemOutput = action.payload;
        },
        setSelectedItemsOutput: (state, action: PayloadAction<number[]>) => {
            state.selectedItemsOutput = action.payload;
        },
        setDataDeletedOutput: (state, action: PayloadAction<ProcessingTableDetail[]>) => {
            state.dataDeletedOutput = action.payload;
        },
        setDataDeletedInput: (state, action: PayloadAction<ProcessingTableDetail[]>) => {
            state.dataDeletedInput = action.payload;
        },
        // List
        setDataTable: (state, action: PayloadAction<ProcessingTable[]>) => {
            state.dataTable = action.payload;
        },
        setPaginateMetaData: (state, action: PayloadAction<Pagination>) => {
            state.paginateMetaData = action.payload;
        },
        setSelectedItem: (state, action: PayloadAction<ProcessingTable | undefined>) => {
            state.selectedItem = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<number[]>) => {
            state.selectedItems = action.payload;
        },
        // Common
        setDetailLayout: (state, action: PayloadAction<boolean>) => {
            state.isDetailLayout = action.payload;
        },
        setShowDeleteButton: (state, action: PayloadAction<boolean>) => {
            state.isShowDeleteButton = action.payload;
        },
        setFormSearch: (state, action: PayloadAction<ProcessingSearch>) => {
            state.formSearch = action.payload;
        },
        handleBackProcessingTask: (state) => {
            state.isDetailLayout = false;
        },
        setMode: (state, action: PayloadAction<SCREEN_TYPE>) => {
            state.screenMode = action.payload;
        },
        setKeywords: (state, action: PayloadAction<string[]>) => {
            state.getKeywords = action.payload;
        },
        setIsSubmit: (state, action: PayloadAction<boolean>) => {
            state.isSubmit = action.payload;
        },
        setSort: (state, action: PayloadAction<string>) => {
            state.sort = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProcessingList.fulfilled, (state, action) => {
                state.dataTable = action.payload.data ?? [];
                state.paginateMetaData = action.payload.pagination || paginateDefault;
                state.selectedItems = [];
                state.isShowDeleteButton = false;
            })
            .addCase(fetchProcessingListDetail.fulfilled, (state, action) => {
                const data: ProcessingTableDetail[] = action.payload ?? [];
                const dataTableInput = data
                    .filter((item) => item.type === TYPE_PROCESSING_TASK_DETAIL.PROCESSING_INPUT)
                    .map((el) => ({
                        ...el,
                        images: [el?.image1, el?.image2, el?.image3, el?.image4, el?.image5].filter(
                            (image): image is string => typeof image === 'string',
                        ),
                    }));

                const dataTableOutput = data
                    .filter((item) => item.type === TYPE_PROCESSING_TASK_DETAIL.PROCESSING_OUTPUT)
                    .map((el) => ({
                        ...el,
                        images: [el?.image1, el?.image2, el?.image3, el?.image4, el?.image5].filter(
                            (image): image is string => typeof image === 'string',
                        ),
                    }));
                state.dataTableInput = dataTableInput;
                state.dataTableOutput = dataTableOutput;
            })
            .addCase(fetchDeleteProcessing.fulfilled, (state) => {
                state.isShowDeleteButton = false;
                state.selectedItems = [];
            })
            .addCase(fetchDeleteProcessing.rejected, (_state, action) => {
                console.error('Delete failed:', action.payload);
            })
            .addCase(updateProcessingOutput.fulfilled, resetProcessingState)
            .addCase(updateProcessingOutput.rejected, resetProcessingState)
            .addCase(updateProcessingIntput.fulfilled, resetProcessingState)
            .addCase(updateProcessingIntput.rejected, resetProcessingState);
    },
});

export const {
    // Detail
    setFormType,
    setFormRegister,
    setDataTableInput,
    setSelectedItemInput,
    setSelectedItemsInput,
    setDataTableOutput,
    setSelectedItemOutput,
    setSelectedItemsOutput,
    setDataDeletedOutput,
    setDataDeletedInput,
    // List
    setDataTable,
    setPaginateMetaData,
    setSelectedItem,
    setSelectedItems,
    // Common
    setDetailLayout,
    setShowDeleteButton,
    setFormSearch,
    handleBackProcessingTask,
    setMode,
    setKeywords,
    setIsSubmit,
    setSort,
} = processingTaskSlice.actions;

export default processingTaskSlice.reducer;
